import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CloseIcon from 'mdi-react/CloseIcon';

const HEX = /^#([0-9a-f]{6}|[0-9a-f]{8})$/i;

// "fff" / "#fff" / "1a2b3c" -> "#FFFFFF" / "#1A2B3C"; anything else as typed
const normalize = (raw) => {
  const text = `${raw || ''}`.trim().replace(/^#?/, '#');
  if (text === '#') return '';
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(text);
  const full = short
    ? `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`
    : text;
  return HEX.test(full) ? full.toUpperCase() : full;
};

/**
 * An optional colour (redux-form field), as one control: a chip that opens
 * the colour picker, the hex value, and × to clear it. Empty = no colour of
 * its own (`emptyLabel` says what is used instead). `presets` are one-click
 * swatches under it. Styles: .color-field in form-card.scss.
 */
const OptionalColorField = ({
  input,
  meta,
  label,
  clearLabel,
  emptyLabel,
  presets,
}) => {
  const value = input.value || '';
  const isHex = HEX.test(value);
  // What's typed, tidied up (and saved) when the field is left
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const set = (next) => input.onChange(normalize(next));
  const showError = meta?.error && (meta?.touched || (draft && !isHex));

  return (
    <div className="color-field">
      <div
        className={`color-field__control${
          showError ? ' color-field__control--error' : ''
        }`}
      >
        <label
          className={`color-field__chip${isHex ? '' : ' color-field__chip--empty'}`}
          style={isHex ? { background: value } : undefined}
          title={label}
        >
          <input
            type="color"
            aria-label={label}
            // The picker can't be empty: white until a colour is chosen
            value={isHex ? value.slice(0, 7) : '#ffffff'}
            onChange={(event) => set(event.target.value)}
          />
        </label>
        <input
          type="text"
          className="color-field__hex"
          aria-label={label}
          value={draft}
          placeholder={emptyLabel}
          spellCheck={false}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            set(draft);
            input.onBlur(normalize(draft));
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              set(draft);
            }
          }}
        />
        {!!value && (
          <button
            type="button"
            className="color-field__clear"
            onClick={() => set('')}
            aria-label={clearLabel}
            title={clearLabel}
          >
            <CloseIcon size={16} />
          </button>
        )}
      </div>
      {presets.length > 0 && (
        <div className="color-field__presets">
          {presets.map((preset) => (
            <button
              key={preset}
              type="button"
              className={`color-field__preset${
                preset.toUpperCase() === value.toUpperCase()
                  ? ' color-field__preset--on'
                  : ''
              }`}
              style={{ background: preset }}
              onClick={() => set(preset)}
              aria-label={preset}
              title={preset}
            />
          ))}
        </div>
      )}
      {showError && (
        <span className="form__form-group-error">{meta.error}</span>
      )}
    </div>
  );
};

OptionalColorField.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
  label: PropTypes.string.isRequired,
  clearLabel: PropTypes.string.isRequired,
  emptyLabel: PropTypes.string,
  presets: PropTypes.arrayOf(PropTypes.string),
};

OptionalColorField.defaultProps = {
  meta: {},
  emptyLabel: '#RRGGBB',
  presets: [],
};

export default OptionalColorField;
